import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Calendar, MapPin, Clock } from "lucide-react";

export const MatchDetail = ({ match }) => {
  return (
    <div className="space-y-6">
      {/* Match Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <Badge variant="secondary">Giornata {match.round}</Badge>
          {match.played ? (
            <Badge variant="outline" className="text-success border-success/50">
              Partita Conclusa
            </Badge>
          ) : (
            <Badge variant="secondary">
              Da giocare
            </Badge>
          )}
        </div>
        
        {/* Score Display or Teams */}
        {match.played ? (
          <div className="grid grid-cols-3 gap-4 items-center mb-6">
            <div className="text-center">
              <div className={`text-2xl sm:text-3xl font-bold mb-2 break-words ${
                match.winner === match.homeTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {match.homeTeam}
              </div>
              {match.winner === match.homeTeam && (
                <Trophy className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto" />
              )}
            </div>
            
            <div className="text-center">
              <div className="text-5xl sm:text-6xl font-bold">
                <span className={match.winner === match.homeTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {match.homeScore}
                </span>
                <span className="text-muted-foreground mx-2">-</span>
                <span className={match.winner === match.awayTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  {match.awayScore}
                </span>
              </div>
            </div>
            
            <div className="text-center">
              <div className={`text-2xl sm:text-3xl font-bold mb-2 break-words ${
                match.winner === match.awayTeam ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              }`}>
                {match.awayTeam}
              </div>
              {match.winner === match.awayTeam && (
                <Trophy className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto" />
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <span className="text-xl sm:text-2xl font-bold text-foreground break-words">
                {match.homeTeam}
              </span>
            </div>
            
            <div className="text-center text-lg text-muted-foreground font-medium">
              VS
            </div>
            
            <div className="text-center p-4 rounded-lg bg-muted/30">
              <span className="text-xl sm:text-2xl font-bold text-foreground break-words">
                {match.awayTeam}
              </span>
            </div>
          </div>
        )}
        
        {/* Match Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-border">
          {match.date && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Calendar className="w-5 h-5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide mb-1">Data</div>
                <div className="font-semibold text-foreground">{match.date}</div>
              </div>
            </div>
          )}
          
          {match.time && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <Clock className="w-5 h-5 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wide mb-1">Orario</div>
                <div className="font-semibold text-foreground">{match.time}</div>
              </div>
            </div>
          )}
          
          {match.venue && (
            <div className="flex items-start gap-3 text-muted-foreground sm:col-span-2">
              <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
              <div className="w-full">
                <div className="text-xs uppercase tracking-wide mb-1">Luogo</div>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(match.venue + (match.venueAddress ? ' ' + match.venueAddress : ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-600 dark:text-blue-400 hover:underline break-words cursor-pointer"
                >
                  {match.venue}
                  {match.venueAddress && (
                    <span className="text-sm text-muted-foreground ml-1">
                      ({match.venueAddress})
                    </span>
                  )}
                </a>
              </div>
            </div>
          )}
        </div>
      </Card>
      
      {/* Set Details */}
      {match.played && match.sets && match.sets.length > 0 && (
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-4">Dettaglio Set</h3>
          <div className="space-y-3">
            {match.sets.map((set, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 rounded-lg bg-muted/30"
              >
                <span className="font-semibold">Set {index + 1}</span>
                <div className="flex items-center gap-4">
                  <span className={`text-2xl font-bold ${
                    set.home > set.away ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {set.home}
                  </span>
                  <span className="text-muted-foreground">-</span>
                  <span className={`text-2xl font-bold ${
                    set.away > set.home ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {set.away}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
